import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UsersIcon, DollarSignIcon, BoxIcon, ClockIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, ArrowLeftIcon, ChevronRightIcon, ShieldIcon, InfoIcon, TruckIcon, MapPinIcon, QrCodeIcon, FileTextIcon, SearchIcon, FilterIcon, CheckIcon, PackageIcon, CalendarIcon } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
interface DeviceAllocation {
  id: string;
  deviceType: string;
  serialNumber: string;
  condition: string;
  grade: 'A' | 'B' | 'C';
  status: 'pending' | 'shipped' | 'delivered' | 'issue';
  estimatedValue: number;
  assignedTo: string;
  shippingInfo?: {
    carrier: string;
    trackingNumber: string;
    estimatedDelivery: string;
  };
}
const SyndicateDistribution: React.FC = () => {
  const navigate = useNavigate();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [activeTab, setActiveTab] = useState('my-allocation');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Mock data for demonstration
  const syndicateData = {
    id: id || 'syn-2023-001',
    name: 'Premium iPhone Collective',
    totalCapital: 100000,
    status: 'active',
    type: 'auction',
    distributionStatus: 'in_progress',
    // 'pending', 'in_progress', 'completed'
    distributionProgress: 65,
    memberContribution: 25000,
    memberShare: 25
  };
  const myDevices: DeviceAllocation[] = [{
    id: 'DEV-001',
    deviceType: 'iPhone 13 Pro (256GB)',
    serialNumber: 'IMEI: 354895/12/839401/2',
    condition: 'Excellent - Minor scratches on frame',
    grade: 'A',
    status: 'shipped',
    estimatedValue: 750,
    assignedTo: 'Alex Johnson',
    shippingInfo: {
      carrier: 'FedEx',
      trackingNumber: '7891234567',
      estimatedDelivery: '2023-08-05'
    }
  }, {
    id: 'DEV-002',
    deviceType: 'iPhone 13 Pro (128GB)',
    serialNumber: 'IMEI: 354895/12/839402/3',
    condition: 'Good - Minor scratches on screen',
    grade: 'B',
    status: 'pending',
    estimatedValue: 680,
    assignedTo: 'Alex Johnson'
  }, {
    id: 'DEV-003',
    deviceType: 'iPhone 12 Pro Max (256GB)',
    serialNumber: 'IMEI: 354895/12/839403/4',
    condition: 'Excellent - Like new',
    grade: 'A',
    status: 'delivered',
    estimatedValue: 620,
    assignedTo: 'Alex Johnson',
    shippingInfo: {
      carrier: 'UPS',
      trackingNumber: '1ZA5678901',
      estimatedDelivery: '2023-07-28'
    }
  }, {
    id: 'DEV-004',
    deviceType: 'iPhone 12 Pro Max (128GB)',
    serialNumber: 'IMEI: 354895/12/839404/5',
    condition: 'Fair - Scratches on back glass',
    grade: 'C',
    status: 'issue',
    estimatedValue: 550,
    assignedTo: 'Alex Johnson'
  }];
  const allDevices: DeviceAllocation[] = [...myDevices, {
    id: 'DEV-005',
    deviceType: 'iPhone 13 Pro (256GB)',
    serialNumber: 'IMEI: 354895/12/839405/6',
    condition: 'Excellent - Minor scratches on frame',
    grade: 'A',
    status: 'shipped',
    estimatedValue: 750,
    assignedTo: 'Maria Garcia'
  }, {
    id: 'DEV-006',
    deviceType: 'iPhone 13 Pro (128GB)',
    serialNumber: 'IMEI: 354895/12/839406/7',
    condition: 'Good - Minor scratches on screen',
    grade: 'B',
    status: 'pending',
    estimatedValue: 680,
    assignedTo: 'James Wilson'
  }, {
    id: 'DEV-007',
    deviceType: 'iPhone 12 Pro Max (256GB)',
    serialNumber: 'IMEI: 354895/12/839407/8',
    condition: 'Excellent - Like new',
    grade: 'A',
    status: 'delivered',
    estimatedValue: 620,
    assignedTo: 'Sarah Miller'
  }, {
    id: 'DEV-008',
    deviceType: 'iPhone 12 Pro Max (128GB)',
    serialNumber: 'IMEI: 354895/12/839408/9',
    condition: 'Fair - Scratches on back glass',
    grade: 'C',
    status: 'issue',
    estimatedValue: 550,
    assignedTo: 'Robert Chen'
  }];
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="warning" data-id="element-3064">Pending</Badge>;
      case 'shipped':
        return <Badge variant="default" data-id="element-3065">Shipped</Badge>;
      case 'delivered':
        return <Badge variant="success" data-id="element-3066">Delivered</Badge>;
      case 'issue':
        return <Badge variant="destructive" data-id="element-3067">Issue</Badge>;
      default:
        return <Badge variant="outline" data-id="element-3068">Unknown</Badge>;
    }
  };
  const getGradeBadge = (grade: string) => {
    switch (grade) {
      case 'A':
        return <Badge variant="success" data-id="element-3069">A Grade</Badge>;
      case 'B':
        return <Badge variant="warning" data-id="element-3070">B Grade</Badge>;
      case 'C':
        return <Badge variant="default" data-id="element-3071">C Grade</Badge>;
      default:
        return <Badge variant="outline" data-id="element-3072">Unknown</Badge>;
    }
  };
  const filteredDevices = (activeTab === 'my-allocation' ? myDevices : allDevices).filter(device => {
    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return device.deviceType.toLowerCase().includes(query) || device.serialNumber.toLowerCase().includes(query) || device.assignedTo.toLowerCase().includes(query);
    }
    return true;
  }).filter(device => {
    // Apply status filter
    if (statusFilter !== 'all') {
      return device.status === statusFilter;
    }
    return true;
  });
  const calculateTotalValue = (devices: DeviceAllocation[]) => {
    return devices.reduce((total, device) => total + device.estimatedValue, 0);
  };
  return <div className="space-y-6" data-id="element-3073">
      <div className="flex items-center justify-between" data-id="element-3074">
        <div className="flex items-center" data-id="element-3075">
          <Button variant="ghost" size="sm" className="mr-2" onClick={() => navigate(`/syndicate-detail/${id}`)} data-id="element-3076">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-3077" />
            Back to Syndicate
          </Button>
          <h1 className="text-2xl font-bold" data-id="element-3078">Inventory Distribution</h1>
          <Badge variant="outline" className="ml-2" data-id="element-3079">
            {syndicateData.name}
          </Badge>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-3080">
        <Card data-id="element-3081">
          <CardContent className="p-4" data-id="element-3082">
            <div className="flex items-center justify-between" data-id="element-3083">
              <div data-id="element-3084">
                <p className="text-sm text-muted-foreground" data-id="element-3085">
                  Distribution Status
                </p>
                <p className="text-2xl font-bold" data-id="element-3086">In Progress</p>
              </div>
              <BoxIcon className="h-8 w-8 text-muted-foreground/30" data-id="element-3087" />
            </div>
            <Progress value={syndicateData.distributionProgress} className="h-1 mt-2" data-id="element-3088" />
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3089">
              {syndicateData.distributionProgress}% Complete
            </p>
          </CardContent>
        </Card>
        <Card data-id="element-3090">
          <CardContent className="p-4" data-id="element-3091">
            <div className="flex items-center justify-between" data-id="element-3092">
              <div data-id="element-3093">
                <p className="text-sm text-muted-foreground" data-id="element-3094">Your Devices</p>
                <p className="text-2xl font-bold" data-id="element-3095">{myDevices.length}</p>
              </div>
              <PackageIcon className="h-8 w-8 text-muted-foreground/30" data-id="element-3096" />
            </div>
            <div className="flex items-center space-x-2 mt-2" data-id="element-3097">
              <span className="text-xs text-muted-foreground" data-id="element-3098">
                Total Value:
              </span>
              <span className="text-xs font-medium" data-id="element-3099">
                {formatCurrency(calculateTotalValue(myDevices))}
              </span>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-3100">
          <CardContent className="p-4" data-id="element-3101">
            <div className="flex items-center justify-between" data-id="element-3102">
              <div data-id="element-3103">
                <p className="text-sm text-muted-foreground" data-id="element-3104">Shipping Status</p>
                <p className="text-2xl font-bold" data-id="element-3105">2 of 4</p>
              </div>
              <TruckIcon className="h-8 w-8 text-muted-foreground/30" data-id="element-3106" />
            </div>
            <div className="flex items-center space-x-2 mt-2" data-id="element-3107">
              <span className="text-xs text-muted-foreground" data-id="element-3108">
                Next Delivery:
              </span>
              <span className="text-xs font-medium" data-id="element-3109">Aug 5, 2023</span>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-3110">
          <CardContent className="p-4" data-id="element-3111">
            <div className="flex items-center justify-between" data-id="element-3112">
              <div data-id="element-3113">
                <p className="text-sm text-muted-foreground" data-id="element-3114">Your Share</p>
                <p className="text-2xl font-bold" data-id="element-3115">
                  {syndicateData.memberShare}%
                </p>
              </div>
              <DollarSignIcon className="h-8 w-8 text-muted-foreground/30" data-id="element-3116" />
            </div>
            <div className="flex items-center space-x-2 mt-2" data-id="element-3117">
              <span className="text-xs text-muted-foreground" data-id="element-3118">
                Contribution:
              </span>
              <span className="text-xs font-medium" data-id="element-3119">
                {formatCurrency(syndicateData.memberContribution)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
      <Card data-id="element-3120">
        <CardHeader className="pb-2" data-id="element-3121">
          <div className="flex items-center justify-between" data-id="element-3122">
            <div data-id="element-3123">
              <CardTitle data-id="element-3124">Device Allocation</CardTitle>
              <CardDescription data-id="element-3125">
                View and manage your allocated devices
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2" data-id="element-3126">
              <div className="relative" data-id="element-3127">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" data-id="element-3128" />
                <input type="text" placeholder="Search devices..." className="pl-10 pr-4 py-2 rounded-md border bg-background text-sm w-56" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3129" />
              </div>
              <select className="py-2 px-3 rounded-md border bg-background text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-3130">
                <option value="all" data-id="element-3131">All Statuses</option>
                <option value="pending" data-id="element-3132">Pending</option>
                <option value="shipped" data-id="element-3133">Shipped</option>
                <option value="delivered" data-id="element-3134">Delivered</option>
                <option value="issue" data-id="element-3135">Issues</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent data-id="element-3136">
          <Tabs defaultValue="my-allocation" value={activeTab} onValueChange={setActiveTab} data-id="element-3137">
            <TabsList className="mb-4" data-id="element-3138">
              <TabsTrigger value="my-allocation" data-id="element-3139">My Allocation</TabsTrigger>
              <TabsTrigger value="all-devices" data-id="element-3140">
                All Syndicate Devices
              </TabsTrigger>
              <TabsTrigger value="shipping-info" data-id="element-3141">
                Shipping Information
              </TabsTrigger>
            </TabsList>
            <TabsContent value="my-allocation" data-id="element-3142">
              {filteredDevices.length > 0 ? <div className="space-y-4" data-id="element-3143">
                  {filteredDevices.map(device => <Card key={device.id} className="overflow-hidden" data-id="element-3144">
                      <div className="flex flex-col md:flex-row" data-id="element-3145">
                        <div className="p-4 md:w-2/3" data-id="element-3146">
                          <div className="flex items-start justify-between" data-id="element-3147">
                            <div data-id="element-3148">
                              <div className="flex items-center" data-id="element-3149">
                                <h3 className="font-medium" data-id="element-3150">
                                  {device.deviceType}
                                </h3>
                                {getGradeBadge(device.grade)}
                                <Badge variant="outline" className="ml-2" data-id="element-3151">
                                  ID: {device.id}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1" data-id="element-3152">
                                {device.serialNumber}
                              </p>
                              <p className="text-sm mt-2" data-id="element-3153">{device.condition}</p>
                            </div>
                            <div data-id="element-3154">{getStatusBadge(device.status)}</div>
                          </div>
                          {device.status === 'shipped' && device.shippingInfo && <div className="mt-4 p-3 bg-muted/30 rounded-md border" data-id="element-3155">
                                <div className="flex items-center" data-id="element-3156">
                                  <TruckIcon className="h-4 w-4 mr-2 text-muted-foreground" data-id="element-3157" />
                                  <span className="text-sm font-medium" data-id="element-3158">
                                    Shipping Information
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mt-2 text-sm" data-id="element-3159">
                                  <div data-id="element-3160">
                                    <span className="text-muted-foreground" data-id="element-3161">
                                      Carrier:
                                    </span>
                                    <span className="ml-1 font-medium" data-id="element-3162">
                                      {device.shippingInfo.carrier}
                                    </span>
                                  </div>
                                  <div data-id="element-3163">
                                    <span className="text-muted-foreground" data-id="element-3164">
                                      Tracking:
                                    </span>
                                    <span className="ml-1 font-medium" data-id="element-3165">
                                      {device.shippingInfo.trackingNumber}
                                    </span>
                                  </div>
                                  <div data-id="element-3166">
                                    <span className="text-muted-foreground" data-id="element-3167">
                                      Estimated Delivery:
                                    </span>
                                    <span className="ml-1 font-medium" data-id="element-3168">
                                      {device.shippingInfo.estimatedDelivery}
                                    </span>
                                  </div>
                                </div>
                              </div>}
                          {device.status === 'issue' && <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800" data-id="element-3169">
                              <div className="flex items-center" data-id="element-3170">
                                <AlertTriangleIcon className="h-4 w-4 mr-2 text-red-500" data-id="element-3171" />
                                <span className="text-sm font-medium" data-id="element-3172">
                                  Issue Reported
                                </span>
                              </div>
                              <p className="text-sm mt-1" data-id="element-3173">
                                There is an issue with this device. Our team is
                                investigating and will contact you shortly.
                              </p>
                            </div>}
                        </div>
                        <div className="p-4 bg-muted/30 border-t md:border-t-0 md:border-l md:w-1/3" data-id="element-3174">
                          <div className="space-y-4" data-id="element-3175">
                            <div data-id="element-3176">
                              <p className="text-sm text-muted-foreground" data-id="element-3177">
                                Estimated Value
                              </p>
                              <p className="text-xl font-bold" data-id="element-3178">
                                {formatCurrency(device.estimatedValue)}
                              </p>
                            </div>
                            <div className="space-y-2" data-id="element-3179">
                              {device.status === 'pending' && <Button className="w-full" data-id="element-3180">
                                  <TruckIcon className="mr-2 h-4 w-4" data-id="element-3181" />
                                  Track Shipment
                                </Button>}
                              {device.status === 'shipped' && <Button className="w-full" data-id="element-3182">
                                  <TruckIcon className="mr-2 h-4 w-4" data-id="element-3183" />
                                  Track Shipment
                                </Button>}
                              {device.status === 'delivered' && <Button className="w-full" variant="outline" data-id="element-3184">
                                  <CheckCircleIcon className="mr-2 h-4 w-4" data-id="element-3185" />
                                  Confirm Receipt
                                </Button>}
                              {device.status === 'issue' && <Button className="w-full" data-id="element-3186">
                                  <AlertTriangleIcon className="mr-2 h-4 w-4" data-id="element-3187" />
                                  View Issue Details
                                </Button>}
                              <Button variant="outline" className="w-full" data-id="element-3188">
                                <FileTextIcon className="mr-2 h-4 w-4" data-id="element-3189" />
                                View Details
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>)}
                </div> : <div className="flex flex-col items-center justify-center p-8 border rounded-md bg-muted/30" data-id="element-3190">
                  <BoxIcon className="h-12 w-12 text-muted-foreground/50 mb-4" data-id="element-3191" />
                  <h3 className="text-lg font-medium mb-2" data-id="element-3192">No Devices Found</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md" data-id="element-3193">
                    {searchQuery || statusFilter !== 'all' ? 'No devices match your current filters. Try adjusting your search criteria.' : "You don't have any devices allocated yet. Devices will appear here once the distribution process begins."}
                  </p>
                  {(searchQuery || statusFilter !== 'all') && <Button variant="outline" className="mt-4" onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
              }} data-id="element-3194">
                      Clear Filters
                    </Button>}
                </div>}
            </TabsContent>
            <TabsContent value="all-devices" data-id="element-3195">
              <div className="overflow-x-auto" data-id="element-3196">
                <table className="w-full" data-id="element-3197">
                  <thead data-id="element-3198">
                    <tr className="border-b" data-id="element-3199">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-3200">
                        Device
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-3201">
                        ID
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-3202">
                        Grade
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-3203">
                        Assigned To
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-3204">
                        Status
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground" data-id="element-3205">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3206">
                    {filteredDevices.map(device => <tr key={device.id} className="border-b hover:bg-muted/50" data-id="element-3207">
                        <td className="py-3 px-4 font-medium" data-id="element-3208">
                          {device.deviceType}
                        </td>
                        <td className="py-3 px-4" data-id="element-3209">{device.id}</td>
                        <td className="py-3 px-4" data-id="element-3210">
                          {getGradeBadge(device.grade)}
                        </td>
                        <td className="py-3 px-4" data-id="element-3211">
                          <div className="flex items-center" data-id="element-3212">
                            <Avatar className="h-6 w-6 mr-2" data-id="element-3213">
                              <AvatarImage src="https://github.com/shadcn.png" data-id="element-3214" />
                              <AvatarFallback data-id="element-3215">
                                {device.assignedTo.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span data-id="element-3216">{device.assignedTo}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4" data-id="element-3217">
                          {getStatusBadge(device.status)}
                        </td>
                        <td className="py-3 px-4 text-right" data-id="element-3218">
                          {formatCurrency(device.estimatedValue)}
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot className="bg-muted/30" data-id="element-3219">
                    <tr data-id="element-3220">
                      <td colSpan={5} className="py-3 px-4 font-medium" data-id="element-3221">
                        Total
                      </td>
                      <td className="py-3 px-4 text-right font-medium" data-id="element-3222">
                        {formatCurrency(calculateTotalValue(filteredDevices))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>
            <TabsContent value="shipping-info" data-id="element-3223">
              <div className="space-y-6" data-id="element-3224">
                <div className="bg-muted/30 p-4 rounded-md border flex items-start" data-id="element-3225">
                  <InfoIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-3226" />
                  <div data-id="element-3227">
                    <p className="text-sm font-medium" data-id="element-3228">Shipping Information</p>
                    <p className="text-sm text-muted-foreground mt-1" data-id="element-3229">
                      All devices will be shipped to your registered address.
                      Please ensure your shipping address is up to date in your
                      profile.
                    </p>
                  </div>
                </div>
                <Card data-id="element-3230">
                  <CardHeader data-id="element-3231">
                    <CardTitle data-id="element-3232">Your Shipping Address</CardTitle>
                  </CardHeader>
                  <CardContent data-id="element-3233">
                    <div className="flex items-start" data-id="element-3234">
                      <MapPinIcon className="h-5 w-5 text-muted-foreground mr-3 mt-0.5 flex-shrink-0" data-id="element-3235" />
                      <div data-id="element-3236">
                        <p className="font-medium" data-id="element-3237">Alex Johnson</p>
                        <p className="text-sm" data-id="element-3238">123 Market Street</p>
                        <p className="text-sm" data-id="element-3239">Apt 4B</p>
                        <p className="text-sm" data-id="element-3240">San Francisco, CA 94105</p>
                        <p className="text-sm" data-id="element-3241">United States</p>
                        <p className="text-sm mt-2" data-id="element-3242">Phone: (415) 555-1234</p>
                      </div>
                    </div>
                    <div className="flex justify-end mt-4" data-id="element-3243">
                      <Button variant="outline" data-id="element-3244">Update Address</Button>
                    </div>
                  </CardContent>
                </Card>
                <Card data-id="element-3245">
                  <CardHeader data-id="element-3246">
                    <CardTitle data-id="element-3247">Shipment Tracking</CardTitle>
                  </CardHeader>
                  <CardContent data-id="element-3248">
                    <div className="space-y-4" data-id="element-3249">
                      {myDevices.filter(device => device.status === 'shipped' && device.shippingInfo).map(device => <div key={device.id} className="p-4 border rounded-md" data-id="element-3250">
                            <div className="flex items-start justify-between" data-id="element-3251">
                              <div data-id="element-3252">
                                <div className="flex items-center" data-id="element-3253">
                                  <h3 className="font-medium" data-id="element-3254">
                                    {device.deviceType}
                                  </h3>
                                  <Badge variant="outline" className="ml-2" data-id="element-3255">
                                    ID: {device.id}
                                  </Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mt-2 text-sm" data-id="element-3256">
                                  <div data-id="element-3257">
                                    <span className="text-muted-foreground" data-id="element-3258">
                                      Carrier:
                                    </span>
                                    <span className="ml-1 font-medium" data-id="element-3259">
                                      {device.shippingInfo?.carrier}
                                    </span>
                                  </div>
                                  <div data-id="element-3260">
                                    <span className="text-muted-foreground" data-id="element-3261">
                                      Tracking:
                                    </span>
                                    <span className="ml-1 font-medium" data-id="element-3262">
                                      {device.shippingInfo?.trackingNumber}
                                    </span>
                                  </div>
                                  <div data-id="element-3263">
                                    <span className="text-muted-foreground" data-id="element-3264">
                                      Estimated Delivery:
                                    </span>
                                    <span className="ml-1 font-medium" data-id="element-3265">
                                      {device.shippingInfo?.estimatedDelivery}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <Button variant="outline" size="sm" data-id="element-3266">
                                Track Package
                              </Button>
                            </div>
                          </div>)}
                      {myDevices.filter(device => device.status === 'shipped' && device.shippingInfo).length === 0 && <div className="flex flex-col items-center justify-center p-8 border rounded-md bg-muted/30" data-id="element-3267">
                          <TruckIcon className="h-12 w-12 text-muted-foreground/50 mb-4" data-id="element-3268" />
                          <h3 className="text-lg font-medium mb-2" data-id="element-3269">
                            No Active Shipments
                          </h3>
                          <p className="text-sm text-muted-foreground text-center max-w-md" data-id="element-3270">
                            You don't have any devices currently being shipped.
                            Shipment information will appear here once your
                            devices are dispatched.
                          </p>
                        </div>}
                    </div>
                  </CardContent>
                </Card>
                <Card data-id="element-3271">
                  <CardHeader data-id="element-3272">
                    <CardTitle data-id="element-3273">Shipping Schedule</CardTitle>
                  </CardHeader>
                  <CardContent data-id="element-3274">
                    <div className="relative pl-6 space-y-6" data-id="element-3275">
                      <div className="absolute left-0 top-2 bottom-0 w-px bg-border" data-id="element-3276"></div>
                      <div className="relative" data-id="element-3277">
                        <div className="absolute left-[-23px] top-1 h-4 w-4 rounded-full bg-green-500 flex items-center justify-center" data-id="element-3278">
                          <CheckIcon className="h-2.5 w-2.5 text-white" data-id="element-3279" />
                        </div>
                        <div className="space-y-1" data-id="element-3280">
                          <h3 className="text-base font-medium" data-id="element-3281">
                            Distribution Started
                          </h3>
                          <p className="text-sm text-muted-foreground" data-id="element-3282">
                            Inventory allocation process began
                          </p>
                          <div className="flex items-center text-xs text-muted-foreground mt-1" data-id="element-3283">
                            <CalendarIcon className="h-3 w-3 mr-1" data-id="element-3284" />
                            <span data-id="element-3285">July 28, 2023</span>
                          </div>
                        </div>
                      </div>
                      <div className="relative" data-id="element-3286">
                        <div className="absolute left-[-23px] top-1 h-4 w-4 rounded-full bg-green-500 flex items-center justify-center" data-id="element-3287">
                          <CheckIcon className="h-2.5 w-2.5 text-white" data-id="element-3288" />
                        </div>
                        <div className="space-y-1" data-id="element-3289">
                          <h3 className="text-base font-medium" data-id="element-3290">
                            First Batch Shipped
                          </h3>
                          <p className="text-sm text-muted-foreground" data-id="element-3291">
                            First devices shipped to members
                          </p>
                          <div className="flex items-center text-xs text-muted-foreground mt-1" data-id="element-3292">
                            <CalendarIcon className="h-3 w-3 mr-1" data-id="element-3293" />
                            <span data-id="element-3294">July 30, 2023</span>
                          </div>
                        </div>
                      </div>
                      <div className="relative" data-id="element-3295">
                        <div className="absolute left-[-23px] top-1 h-4 w-4 rounded-full bg-primary" data-id="element-3296"></div>
                        <div className="space-y-1" data-id="element-3297">
                          <h3 className="text-base font-medium" data-id="element-3298">
                            Second Batch Shipping
                          </h3>
                          <p className="text-sm text-muted-foreground" data-id="element-3299">
                            Remaining devices being prepared for shipment
                          </p>
                          <div className="flex items-center text-xs text-muted-foreground mt-1" data-id="element-3300">
                            <CalendarIcon className="h-3 w-3 mr-1" data-id="element-3301" />
                            <span data-id="element-3302">August 3, 2023</span>
                          </div>
                        </div>
                      </div>
                      <div className="relative" data-id="element-3303">
                        <div className="absolute left-[-23px] top-1 h-4 w-4 rounded-full bg-muted-foreground/30" data-id="element-3304"></div>
                        <div className="space-y-1" data-id="element-3305">
                          <h3 className="text-base font-medium" data-id="element-3306">
                            Distribution Complete
                          </h3>
                          <p className="text-sm text-muted-foreground" data-id="element-3307">
                            All devices delivered to syndicate members
                          </p>
                          <div className="flex items-center text-xs text-muted-foreground mt-1" data-id="element-3308">
                            <CalendarIcon className="h-3 w-3 mr-1" data-id="element-3309" />
                            <span data-id="element-3310">Estimated: August 10, 2023</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      <div className="flex justify-between" data-id="element-3311">
        <Button variant="outline" onClick={() => navigate(`/syndicate-detail/${id}`)} data-id="element-3312">
          <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-3313" />
          Back to Syndicate Details
        </Button>
        <Button data-id="element-3314">
          <ShieldIcon className="mr-2 h-4 w-4" data-id="element-3315" />
          Report an Issue
        </Button>
      </div>
    </div>;
};
export default SyndicateDistribution;